import React from "react";
import { useTranslation } from "react-i18next";

const EventStatistics = ({ statistics, onClose }) => {
  const { t } = useTranslation(["events"]);

  const getGenderIcon = (gender) => {
    switch (gender) {
      case "MALE":
        return { icon: "fa-mars", color: "primary" };
      case "FEMALE":
        return { icon: "fa-venus", color: "danger" };
      default:
        return { icon: "fa-genderless", color: "secondary" };
    }
  };

  const formatPercentage = (value) => {
    return parseFloat(value).toFixed(2).replace(/\.?0+$/, "");
  };

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-chart-bar me-2"></i>
              {t("statistics.title")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              {/* Attendance by Grade */}
              <div className="col-md-6 mb-4">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body">
                    <h6 className="fw-bold mb-3">
                      <i className="fas fa-graduation-cap text-primary me-2"></i>
                      {t("statistics.attendanceByGrade")}
                    </h6>
                    {statistics.attendanceByGrade &&
                    Object.keys(statistics.attendanceByGrade).length > 0 ? (
                      <div className="row">
                        {Object.entries(statistics.attendanceByGrade).map(
                          ([grade, count]) => (
                            <div key={grade} className="col-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div
                                  className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{ width: "35px", height: "35px" }}
                                >
                                  <span className="text-white fw-bold">
                                    {grade}
                                  </span>
                                </div>
                                <div>
                                  <div className="fw-bold fs-5">{count}</div>
                                  <small className="text-muted">
                                    {t("statistics.grade")} {grade}
                                  </small>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-muted">{t("statistics.noGradeData")}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Attendance by Gender */}
              <div className="col-md-6 mb-4">
                <div className="card border-0 bg-light h-100">
                  <div className="card-body">
                    <h6 className="fw-bold mb-3">
                      <i className="fas fa-users text-info me-2"></i>
                      {t("statistics.attendanceByGender")}
                    </h6>
                    {statistics.attendanceByGender &&
                    Object.keys(statistics.attendanceByGender).length > 0 ? (
                      <div className="row">
                        {Object.entries(statistics.attendanceByGender).map(
                          ([gender, count]) => {
                            const genderConfig = getGenderIcon(gender);
                            return (
                              <div key={gender} className="col-6 mb-3">
                                <div className="d-flex align-items-center">
                                  <div
                                    className={`bg-${genderConfig.color} rounded-circle d-flex align-items-center justify-content-center me-3`}
                                    style={{ width: "35px", height: "35px" }}
                                  >
                                    <i
                                      className={`fas ${genderConfig.icon} text-white`}
                                    ></i>
                                  </div>
                                  <div>
                                    <div className="fw-bold fs-5">{count}</div>
                                    <small className="text-muted">
                                      {gender}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      <p className="text-muted">{t("statistics.noGenderData")}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="col-md-12 mb-4">
                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <h6 className="fw-bold mb-3">
                      <i className="fas fa-tachometer-alt text-success me-2"></i>
                      {t("statistics.keyMetrics")}
                    </h6>
                    <div className="row">
                      {/* Actual Attendance Rate */}
                      <div className="col-md-3 mb-3">
                        <div className="text-center">
                          <div
                            className="bg-success rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <i className="fas fa-percentage text-white"></i>
                          </div>
                          <div className="fw-bold fs-4 text-success">
                            {statistics.actualAttendanceRate
                              ? formatPercentage(
                                  statistics.actualAttendanceRate
                                )
                              : 0}
                            %
                          </div>
                          <small className="text-muted">
                            {t("statistics.actualAttendanceRate")}
                          </small>
                        </div>
                      </div>

                      {/* Club Membership Participation */}
                      <div className="col-md-3 mb-3">
                        <div className="text-center">
                          <div
                            className="bg-info rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <i className="fas fa-users text-white"></i>
                          </div>
                          <div className="fw-bold fs-4 text-info">
                            {statistics.clubMembershipParticipationRate
                              ? formatPercentage(
                                  statistics.clubMembershipParticipationRate
                                )
                              : 0}
                            %
                          </div>
                          <small className="text-muted">
                            {t("statistics.clubMemberParticipation")}
                          </small>
                        </div>
                      </div>

                      {/* Own Campus Participation */}
                      <div className="col-md-3 mb-3">
                        <div className="text-center">
                          <div
                            className="bg-primary rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <i className="fas fa-building text-white"></i>
                          </div>
                          <div className="fw-bold fs-4 text-primary">
                            {statistics.ownCampusParticipationRate
                              ? formatPercentage(
                                  statistics.ownCampusParticipationRate
                                )
                              : 0}
                            %
                          </div>
                          <small className="text-muted">
                            {t("statistics.ownCampusParticipation")}
                          </small>
                        </div>
                      </div>

                      {/* Total Participation */}
                      <div className="col-md-3 mb-3">
                        <div className="text-center">
                          <div
                            className="bg-warning rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                            style={{ width: "50px", height: "50px" }}
                          >
                            <i className="fas fa-chart-line text-white"></i>
                          </div>
                          <div className="fw-bold fs-4 text-warning">
                            {statistics.attendanceByGender
                              ? Object.values(
                                  statistics.attendanceByGender
                                ).reduce((sum, count) => sum + count, 0)
                              : 0}
                          </div>
                          <small className="text-muted">
                            {t("statistics.totalParticipants")}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Campus Participation Rates */}
              {statistics.campusParticipationRates &&
                Object.keys(statistics.campusParticipationRates).length > 0 && (
                  <div className="col-md-12">
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        <h6 className="fw-bold mb-3">
                          <i className="fas fa-map-marker-alt text-warning me-2"></i>
                          {t("statistics.campusParticipationRates")}
                        </h6>
                        <div className="row">
                          {Object.entries(
                            statistics.campusParticipationRates
                          ).map(([campus, rate]) => (
                            <div key={campus} className="col-md-6 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-medium">{campus}</span>
                                <span className="text-muted">
                                  {formatPercentage(rate)}%
                                </span>
                              </div>
                              <div
                                className="progress"
                                style={{ height: "8px" }}
                              >
                                <div
                                  className="progress-bar bg-warning"
                                  style={{
                                    width: `${formatPercentage(rate)}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventStatistics;
